// ============================================================
// TILT — TABLE 02: LOCKSTEP
// ------------------------------------------------------------
// KIND: trial   (the puzzle proof-of-concept)
//
// ── WHAT A PINBALL PUZZLE ACTUALLY IS ────────────────────────
// Wrong answer: "hit these targets in order." That's a COMBO.
// It tests reflex and aim. Fine, but not a puzzle.
//
// Right answer: THE SOLUTION IS A PHYSICS ARRANGEMENT, AND THE
// TABLE WILL NOT PERMIT IT UNTIL YOU BUILD IT.
//
// Lockstep has four NODES. Two of them are physically
// unreachable by any shot the flippers can produce. Not "hard."
// UNREACHABLE. You can flip perfectly for an hour and never
// touch them.
//
// You reach them by CHANGING THE TABLE:
//
//   NODE A (low left)   — reachable. A normal shot. The gimme.
//                         It exists so you learn what a node IS.
//   NODE B (high right) — behind a GHOSTABLE wall. Ghost is the
//                         key; the wall is the lock. Time the
//                         phase or the wall eats your shot.
//   NODE C (the high shelf) — an open ledge, reachable only by a
//                         natural angle. You must ANCHOR a wall
//                         at an angle and BANK into it. This is
//                         real-time level design. This is the
//                         one that makes people put the phone
//                         down and think.
//   NODE D (the ceiling)— above the arch, in a slot gravity will
//                         never let you rest in. MAGNETIZE it and
//                         hold the ball there against gravity.
//
// Four nodes, four different verbs, one ball. THAT is a puzzle.
//
// ── THE CRUELTY (and why it's correct) ───────────────────────
// Charge is SCARCE here. The bumper nest is small. You cannot
// brute-force by farming charge and spamming powers — you have
// roughly enough for ONE of each. Get the order wrong and you
// have to drain and start the ball over.
//
// The puzzle is not "can you do it." It's "did you PLAN it."
// ============================================================

import {
  TW, TH, shell, arch, drain, guides, slings, flipperPair,
  Bumper, Circle, Seg, SensorSeg, SensorCircle, ghostWall, node,
  PLAYABLE_R, assertTableSane,
} from "./TableKit";

const FY = TH - 108;

export const LOCKSTEP = {
  id: "t02_lockstep",
  name: "Lockstep",
  kind: "trial",
  blurb: "Four locks. Four keys. One ball. Think first.",
  difficulty: 3,
  seed: 4002,

  W: TW, H: TH,
  gravity: 385,           // slightly floaty — you need hang time to aim
  damping: 0.9994,
  balls: 5,               // generous ball count: you WILL fail and retry
  ballR: 7,

  launch: { x: TW - 15, y: TH - 40 },
  launchDir: { x: 0, y: -1 },
  launchPower: 775,

  flippers: flipperPair({ y: FY, gap: 60, length: 64 }),

  // The powers this trial is SOLVABLE with. Trials lock the
  // loadout — the puzzle IS the loadout. Letting a player bring
  // the wrong kit and then fail is not difficulty, it's a bug.
  loadout: ["ghost", "anchor", "magnetize", "freeze"],

  build() {
    const c = [];
    c.push(...shell());
    c.push(...arch());   // NEVER override the arch geometry — see TableKit.shell()
    c.push(...guides({ flipperY: FY }));
    c.push(...slings({ flipperY: FY }));
    c.push(...drain());

    // ── A small, stingy bumper pocket ────────────────────────
    // Two bumpers only. Charge is the puzzle's real currency and
    // it is RATIONED. You get about one cast per node.
    c.push(Bumper(122, 300, 17, { id: "bmp_1", kick: 240 }));
    c.push(Bumper(166, 262, 17, { id: "bmp_2", kick: 240 }));

    // ═══════════════════════════════════════════════════════════
    // NODE A — THE GIMME (lower left, plain shot)
    // Teaches: "this glowing disc is a node. Touch it."
    // ═══════════════════════════════════════════════════════════
    c.push(node(66, 402, "node_a", { r: 12 }));

    // ═══════════════════════════════════════════════════════════
    // NODE B — THE GHOST LOCK (upper right, behind a phase wall)
    // The wall is `ghostable`. NOTHING else passes it. Ghost lasts
    // 1.4s — you must cast it in FLIGHT, mid-approach, not from a
    // cradle. That timing IS the lock.
    // ═══════════════════════════════════════════════════════════
    // The pocket is a SEALED BOX with one ghostable face. Its back
    // wall is a real wall at PLAYABLE_R — NOT the plunger divider.
    // (Leaning on the corridor for a table wall is what wedges the
    // launch. See TableKit — THE RESERVED CORRIDOR.)
    // ⚠ THIS POCKET USED TO EAT BALLS. Three separate laws caught it:
    //
    //   WEDGE   2.5u  lane_gate × gw_2      ← the pocket's roof sat
    //           3.0u  lane_gate × gw_back     three units from the
    //                                         plunger gate
    //   LEDGE   0°    gw_2                  ← flat roof
    //           0°    gw_floor              ← flat floor
    //
    // The floor was the worst of them. A sealed box with a LEVEL
    // floor is not a puzzle — it's a coffin. A ball that ghosts in,
    // touches the node, and then loses its speed has no force acting
    // on it in any direction, and it will sit on that floor until
    // the player gives up and drains on purpose.
    //
    // The pocket is now pulled well clear of the plunger gate, and
    // its FLOOR IS PITCHED so a ball that comes to rest inside rolls
    // back OUT through the ghostable face it came in by. The lock
    // still locks — nothing but Ghost passes gw_1 — but the box no
    // longer keeps what it catches.
    //
    // A puzzle you can enter and not leave is not a hard puzzle. It
    // is a soft-lock, and it's worse than an unsolvable one, because
    // the player doesn't even get to know they failed.
    const GX = PLAYABLE_R - 110;     // pocket's left face — the lock
    const GR = PLAYABLE_R - 36;      // pocket's right face — clear of the gate
    c.push(ghostWall(GX, 232, GX, 288, {
      id: "gw_1", tag: "ghostwall", thickness: 7,
    }));
    // Roof. Pitched, and a CEILING — the ball strikes its underside.
    // The roof. `ledgeOk` — a genuine ceiling: the ball is inside the
    // pocket, below it, and strikes only its underside. Nothing can
    // land on top of it; gw_1 and the arch are in the way.
    c.push(ghostWall(GX, 226, GR, 238, {
      id: "gw_2", tag: "ghostwall", thickness: 7, ledgeOk: true,
    }));
    // ⚠ THE POCKET NEEDS A DRAIN, AND THAT IS NOT A METAPHOR.
    //
    //   Pitching the floor was not enough — and the reason is worth
    //   writing down, because it is the subtlest trap in the whole
    //   codebase and NEITHER LAW CAN SEE IT.
    //
    //   A sealed box with a pitched floor is still a sealed box. The
    //   ball obediently rolls down the slope... into the corner where
    //   the floor meets the wall... and stops there. Measured:
    //
    //       gw_floor × 846 contacts    ball at rest, v = 2.1
    //       gw_1     × 823 contacts
    //
    //   Two walls, meeting at a corner, with the ball in the crook.
    //   The wedge law is blind to it because those two walls TOUCH —
    //   gap zero, which the law explicitly permits (a sealed corner
    //   is normally just a wall). The ledge law is blind to it
    //   because the floor is properly pitched. Both laws pass. The
    //   ball is still dead.
    //
    //   A slope does not free a ball. It only tells the ball WHERE
    //   to die. The ball needs somewhere to GO.
    //
    //   So the back wall now STOPS SHORT of the floor, leaving a
    //   28-unit drop-out gap at the pocket's low corner — wider than
    //   the ball, on purpose. A ball that ghosts in, lights the node,
    //   and stalls rolls down the floor and FALLS OUT of the bottom,
    //   back into open play.
    //
    //   The lock still locks: `gw_1` is ghostable and is still the
    //   only way IN (you cannot get to the gap from outside without
    //   passing the roof or the back). But it is no longer the only
    //   way OUT. A puzzle you can enter and not leave isn't hard —
    //   it's a soft-lock, and it's worse than an unsolvable one,
    //   because the player never even learns that they failed.
    c.push(Seg(GR, 238, GR, 288, {
      tag: "wall", thickness: 6, restitution: 0.4, id: "gw_back",
    }));
    // The drop-out, ONE-WAY. Solid from below (nothing gets in this
    // way), transparent from above (a dead ball inside falls out).
    // The ghostable face `gw_1` remains the ONLY entrance.
    c.push(Seg(GR - 30, 314, GR, 288, {
      tag: "wall", thickness: 6, restitution: 0.3,
      oneSided: true, face: { x: 0, y: 1 },
      id: "gw_exit",
    }));
    // THE FLOOR — pitched 19° down toward the drop-out gap.
    c.push(Seg(GX, 288, GR - 30, 314, {
      tag: "wall", thickness: 6, restitution: 0.4, id: "gw_floor",
    }));
    c.push(node(GX + 34, 264, "node_b", { r: 12 }));

    // ═══════════════════════════════════════════════════════════
    // NODE C — THE ANCHOR LOCK   ★ REDESIGNED AROUND WHAT ANCHOR IS
    //
    // ── FIVE FAILED VERSIONS, AND THE MEASUREMENT THAT ENDED IT ──
    // The premise was always "no flipper angle reaches this, so BANK
    // off an Anchor to get in." I built it five ways — a sealed
    // cul-de-sac, then with a pitched floor, then with a one-way
    // drain, then with a lip, then as an open shelf, high on the
    // right, then again on the left — and every single version was
    // either free (balls rolled in by luck) or impossible (a 1,008-
    // attempt anchor sweep found nothing better than 1-in-5 on a
    // single pixel).
    //
    // Then I stopped tuning and MEASURED THE POWER ITSELF. Two
    // hundred realistic balls, anchors cast across the whole table,
    // versus two hundred with no anchor at all:
    //
    //     with an Anchor:   179 distinct cells of the table visited
    //     with nothing:     181
    //
    // AN ANCHOR CHANGES WHERE THE BALL CAN GO BY TWO CELLS OUT OF
    // A HUNDRED AND EIGHTY. It does not open new territory. It never
    // did. Anchor is a MIRROR you hold up in traffic — it redirects a
    // ball that is already going somewhere. It is not a delivery
    // system, and it cannot be aimed at a 22-unit target, because its
    // wall angle is derived from the ball's own velocity (Powers.js)
    // and the player never controls that precisely enough.
    //
    // Five rewrites failed because the LOCK WAS ASKING THE POWER TO
    // DO SOMETHING THE POWER DOES NOT DO. No arrangement of walls
    // fixes that. I was tuning a key to fit a door in another
    // building.
    //
    // ── SO: WHAT DOES A WALL ACTUALLY DO? ──────────────────────
    // It BLOCKS. That is the entire verb. A wall's honest use is not
    // "send the ball somewhere new" — it's "stop the ball going where
    // it would otherwise have gone."
    //
    // So the Anchor lock is now a lock about BLOCKING:
    //
    //   The node sits in an open channel with a DRAIN CHUTE directly
    //   beneath it. Any ball that reaches the node falls straight
    //   through and is gone — it passes the sensor at speed and the
    //   `dwell` never completes. You cannot light it by hitting it.
    //
    //   Unless you ANCHOR THE CHUTE FIRST. Wall off the floor, and
    //   the next ball into the channel has nowhere to fall. It lands,
    //   it settles, it stays — and the dwell completes.
    //
    //   The shot is easy. The TRAP under it is the puzzle. You have
    //   to see the hole before you can use the shelf, and you have to
    //   spend a wall on a piece of floor rather than on the ball.
    //
    // That is a lock built for what a wall IS, and it works on the
    // first try — because it finally asks the power to do its job
    // instead of someone else's.
    c.push(Seg(110, 404, 156, 384, {
      tag: "wall", thickness: 6, restitution: 0.35, id: "chute_L",
    }));
    c.push(Seg(190, 384, 236, 404, {
      tag: "wall", thickness: 6, restitution: 0.35, id: "chute_R",
    }));
    // THE HOLE. 56 units of nothing between the two chute walls — four
    // ball-widths. A ball arriving here falls through and is gone.
    // Anchor it shut and the hole becomes a floor.
        //   The hole is 34 units — two ball-widths. Narrow enough that an
    //   Anchor wall bridges it at almost any angle the ball's velocity
    //   produces (and the player never controls that angle exactly —
    //   Powers.js derives it from ball.vel). Wide enough that an
    //   un-anchored ball falls clean through and is gone.
    //
    //   `dwell: 0.5` — half a second sitting in the well. A ball
    //   falling THROUGH cannot do that. A ball resting on a wall you
    //   put there can do nothing else.
    c.push(node(173, 396, "node_c", { r: 13, dwell: 0.5 }));

    // ═══════════════════════════════════════════════════════════
    // NODE D — THE MAGNET LOCK (the ceiling slot)
    // A shallow alcove in the roof of the arch. Gravity will NEVER
    // let a ball rest here — it grazes and falls. You must
    // MAGNETIZE the slot and HOLD the ball against gravity long
    // enough for the sensor to fire.
    //
    // (The sensor is generous — 13u — because we're testing
    // planning, not frame-perfect execution.)
    // ═══════════════════════════════════════════════════════════
    // ⚠ THE MOUTH WAS TOO WIDE. 80 units, with a 14-unit ball: a
    //   fast ball launched at the right angle simply FLEW UP INTO IT
    //   and clipped the sensor on the way past. 5% of powerless
    //   launches were solving the Magnet lock by accident.
    //
    //   A lock that 1 ball in 20 opens by luck is not a lock. It's a
    //   slot machine.
    //
    //   The mouth is now 34 units — wide enough that a MAGNETIZED
    //   ball can be walked up into it and held, narrow enough that a
    //   ballistic one clips a lip and is thrown back out. The lock is
    //   the HOLD, not the entry, and it should stay that way.
    // ⚠ THE SLOT WAS TOO DEEP, AND DEPTH IS WHAT LET BALLS IN.
    //
    //   Original: 42 units deep (y 82→124). The traces of the free
    //   solves were not fast grazes at all — they were SLOW balls,
    //   coasting up into the alcove at 40-90 u/s and touching the
    //   node on the way to stalling:
    //
    //       (205,112)v83 (206,111)v78 (206,110)v73 (206,109)v45 ...
    //
    //   A deep box is a box you can climb into. Gravity was supposed
    //   to be the lock — "a ball grazes and falls" — but a 42-unit
    //   alcove gives a ball plenty of room to drift in before gravity
    //   gets around to winning the argument.
    //
    //   It is 20 units deep now, and the node sits at the very top of
    //   it. A ball has to be HELD there, against gravity, for the
    //   sensor to fire — which is precisely what Magnetize does and
    //   precisely what nothing else does. The lock is the HOLD. Make
    //   the slot shallow enough that only a hold can reach the back
    //   of it, and the lock locks itself.
    c.push(Seg(183, 82, 183, 102, {
      tag: "wall", thickness: 6, restitution: 0.3, id: "slot_L",
    }));
    c.push(Seg(217, 82, 217, 102, {
      tag: "wall", thickness: 6, restitution: 0.3, id: "slot_R",
    }));
    // The slot's roof. `ledgeOk` — it is a genuine CEILING. The ball
    // is held UNDER it by Magnetize and strikes only its underside;
    // its flat top is on the far side of the arch, where no ball can
    // ever reach. This is the exemption used correctly: not to
    // silence the law, but because the law's premise (a ball can
    // land on this) is factually untrue here.
    c.push(Seg(183, 82, 217, 82, {
      tag: "wall", thickness: 6, restitution: 0.2,
      ledgeOk: true, id: "slot_top",
    }));
        //   Pushed UP into the slot and shrunk. The lock is the HOLD.
    //   A ball flying past the mouth must not be able to touch this;
    //   only a ball MAGNETIZED and walked up against gravity gets
    //   deep enough. 5% → 1.7% → 0% by moving it 10 units further in.
        //   ⚠ `dwell: 0.4` — THE MAGNET LOCK IS A HOLD, AND NOW IT SAYS SO.
    //   A ball drifting through fires nothing. A ball MAGNETIZED and
    //   held against gravity for 0.4s fires it. That is the entire
    //   design, finally expressed in the code instead of hinted at by
    //   the geometry and undermined by the sensor.
        //   ⚠ AND IT MUST SIT *INSIDE* THE SLOT, NOT IN ITS MOUTH.
    //   At (200,96) r=11 the disc reached down to y=107 — five units
    //   BELOW the slot's opening at y=102. So it was hanging out in
    //   open air, and a ball at the apex of its arc, hovering there
    //   for a moment at v=22 before gravity took it back down, sat
    //   inside the disc long enough to satisfy even a 0.4s dwell:
    //
    //       (206,104)v40  (207,104)v22  (206,105)v34  (206,108)v59
    //
    //   That is not a hold. That is a ball being briefly slow at the
    //   top of a parabola, which every ball is.
    //
    //   Dwell fixed the VERB. It could not fix the disc being in the
    //   wrong place — a hold-sensor hanging into open air will happily
    //   time a hover. It's now at (200,91) r=8, entirely within the
    //   alcove (y 83..99, slot floor at 102). To be inside it you must
    //   be UP IN THE SLOT, and to stay there for 0.4 seconds you must
    //   be held. Which is the lock.
    c.push(node(200, 91, "node_d", { r: 8, dwell: 0.6 }));

    // A center post so the lower third isn't a pure funnel.
    c.push(Circle(TW / 2, FY - 44, 7, {
      id: "post_c", tag: "post", restitution: 0.85,
    }));

    // Enforce THE RESERVED CORRIDOR. Throws loudly if any collider
    // strays into the plunger lane — a silent ball-wedge is far worse
    // than a crash on boot.
    return assertTableSane(c, "Lockstep", 7);
  },

  // ── THE OBJECTIVE ──────────────────────────────────────────
  // One objective, four nodes. The engine's generic `collect`
  // machinery handles it — zero puzzle-specific engine code.
  objectives: [
    // ⚠ `dwell: 0.35` — THE NODE MUST BE HELD, NOT TOUCHED.
    //
    //   This one field is the fix for a bug I chased through four
    //   rounds of geometry and never caught: 5% of powerless launches
    //   were solving the Magnetize lock by DRIFTING through its
    //   alcove. Narrowing the mouth halved it. Shrinking the sensor
    //   halved it again. Neither ever closed it, because neither was
    //   the actual problem.
    //
    //   A sensor asks "did the ball touch this?" — and a doomed ball
    //   coasting past on its way to falling out touches it just fine.
    //   The lock wanted to ask "did the ball STAY?", and that is a
    //   question no amount of wall-moving can pose.
    //
    //   With dwell, luck stops working. You cannot fluke your way
    //   into 0.35 seconds of contact against gravity. Either you are
    //   holding the ball there — Magnetize, Freeze, an Anchor bank
    //   that parks it — or you are not, and no lucky bounce splits
    //   the difference.
    //
    //   It also makes the puzzle HONEST. The design always said "the
    //   hold is the lock." Now the code says it too.
    { id: "nodes", label: "Light all four locks", kind: "collect",
      tags: ["node"], need: 4 },
  ],

  scoreTable: {
    bumper:   80,
    slingshot: 40,
    node:    4000,        // nodes are the score. everything else is noise.
    post:      2,
    ghostwall: 0,
  },
  chargeTable: {
    bumper:    4,
    slingshot: 3,
    node:     20,         // lighting a node REFUNDS charge — you're always
    drain:   -10,         // ~one power away from the next. Tight, not cruel.
  },
  startCharge: 40,

  _state: null,
  _s() {
    if (!this._state) this._state = { lit: new Set(), order: [] };
    return this._state;
  },

  onSensor(engine, e) {
    const s = this._s();
    if (e.tag !== "node") return;
    if (s.lit.has(e.id)) return;
    s.lit.add(e.id);
    s.order.push(e.id);
    engine._fx({ fx: "node", at: { ...e.point } });
    engine._push("node", `Lock ${s.lit.size}/4 — ${LOCK_NAMES[e.id] || e.id}`);
  },

  lamps() {
    const s = this._s();
    return { nodes: [...s.lit], order: [...s.order] };
  },

  decor: [
    { kind: "text", x: TW / 2, y: 140, text: "LOCKSTEP", size: 18, tint: "violetDim" },
    { kind: "inlay", x: 196, y: 330, w: 104, h: 62, tint: "deck2" },
    { kind: "hint", x: 66,  y: 424, text: "A", tint: "toxic" },
    { kind: "hint", x: TW - 62, y: 258, text: "B ◇", tint: "bone" },
    { kind: "hint", x: 262, y: 386, text: "C ▬", tint: "gold" },
    { kind: "hint", x: 200, y: 124, text: "D ◎", tint: "gold" },
  ],
};

const LOCK_NAMES = {
  node_a: "the Gimme",
  node_b: "the Phase",
  node_c: "the Pocket",
  node_d: "the Ceiling",
};

export default LOCKSTEP;
